import { useAuthState } from '@auth/hooks';
import { authRoutes } from '@auth/routes';
import { CommunityDialog } from '@communities/community/components/CommunityDialog';
import type { Community } from '@communities/community/domain';
import { useCommunities } from '@communities/community/hooks';
import { CommunityGroupLoadingState, CommunityGroupAuthRequired } from '@communities/community-groups/components';
import { useCommunityGroupDetail } from '@communities/community-groups/hooks';
import * as styles from '@communities/community-groups/pages/CommunityGroupDetailPage.css';
import { EntityType } from '@communities/content-management/domain';
import { TabNavigation } from '@communities/content-management/pages';
import { routes } from '@communities/routes';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { ErrorDisplay, HeroBackground, MessageToast } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import type { DomainError } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CommunityGroupHeaderSection } from './sections/CommunityGroupHeaderSection';

/** Build a prompt warning message from domain errors */
function buildPromptWarningMessage(warnings: DomainError[]): string {
  return warnings.map(w => w.userMessage).join('; ') || 'Some prompts could not be generated. You may need to regenerate them manually.';
}

/**
 * Community Group Detail Page Component
 *
 * Displays detailed information about a community group and its communities
 */

// eslint-disable-next-line complexity
function CommunityGroupDetailPageComponent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthState();
  const { data: communityGroup, isCommunityGroupAdmin, loading, errors } = useCommunityGroupDetail();
  const { data: communities, loading: _communitiesLoading, errors: _communitiesErrors } = useCommunities();

  // Feature flag for community group objects
  const isCommunityGroupObjectsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_GROUP_OBJECTS);

  // Dialog and toast states
  const [isAddCommunityDialogOpen, setIsAddCommunityDialogOpen] = useState(false);
  const [promptWarning, setPromptWarning] = useState<string | null>(null);

  const handlePromptWarnings = useCallback((warnings: DomainError[]) => setPromptWarning(buildPromptWarningMessage(warnings)), []);
  const handleSelectCommunity = useCallback((community: Community) => navigate(routes.communities.detail(community.id)), [navigate]);

  // Check for action query parameter to auto-open create community dialog
  useEffect(() => {
    const action = searchParams.get('action');

    if (action === 'create-community' && !isAddCommunityDialogOpen) {
      setIsAddCommunityDialogOpen(true);
      // Remove the query parameter after opening the dialog
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isAddCommunityDialogOpen]);

  // Navigate to not-found page when community group data is null after loading completes.
  // Uses useEffect to ensure React Router navigation is processed as a side effect,
  // not during render — calling navigate() during render is unreliable in React 18+.
  // Uses { replace: true } so the invalid URL is replaced in history — without this,
  // the "Go Back" button on /not-found would return to the invalid group URL, which
  // would immediately redirect back to /not-found in an infinite loop.
  const shouldShowNotFound = !loading && !communityGroup && !(Array.isArray(errors) ? errors.length > 0 : Boolean(errors));

  useEffect(() => {
    if (shouldShowNotFound) {
      navigate(authRoutes.notFound, { replace: true });
    }
  }, [shouldShowNotFound, navigate]);

  // Authentication check
  if (!isAuthenticated) {
    return <CommunityGroupAuthRequired />;
  }

  // Show error state when errors exist and no data
  if (errors && !communityGroup && !loading) {
    return (
      <ErrorDisplay
        isOpen
        correlationId={errors[0]?.correlationId}
        message={formatErrorMessages(errors)}
        title="Unable to Load Community Group"
        onBack={() => navigate(routes.communityGroups.root)}
        onClose={() => navigate(routes.communityGroups.root)}
      />
    );
  }

  // Render null while redirect to not-found is in progress
  if (shouldShowNotFound) {
    return null;
  }

  return (
    <div>
      <HeroBackground imageUrl={undefined} />

      {loading ? (
        <CommunityGroupLoadingState />
      ) : !communityGroup ? null : (
        <>
          {/* Header Section with Background Image */}
          <CommunityGroupHeaderSection
            communities={communities}
            communityGroup={communityGroup}
            isCommunityGroupAdmin={isCommunityGroupAdmin}
            onAddCommunity={() => setIsAddCommunityDialogOpen(true)}
            onSelectCommunity={handleSelectCommunity}
          />

          {/* Content Section with Solid Background */}
          {isCommunityGroupObjectsEnabled && (
            <div className={styles.contentSection}>
              <div className={styles.tabContent}>
                <TabNavigation entityId={communityGroup.id} entityType={EntityType.COMMUNITY_GROUP} isEntityAdmin={isCommunityGroupAdmin} />
              </div>
            </div>
          )}
        </>
      )}

      {/* Community Dialog */}
      {communityGroup && (
        <CommunityDialog
          groupId={communityGroup.id}
          isOpen={isAddCommunityDialogOpen}
          onClose={() => setIsAddCommunityDialogOpen(false)}
          onPromptWarnings={handlePromptWarnings}
        />
      )}

      {/* Prompt Warning Toast */}
      {promptWarning && <MessageToast message={promptWarning} variant="warning" onClose={() => setPromptWarning(null)} />}
    </div>
  );
}

export const CommunityGroupDetailPage = withAppInsightsTracking(CommunityGroupDetailPageComponent, 'CommunityGroupDetailPage');
